import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { creditsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Trophy, Medal, Crown, Zap, Users, Copy, 
  Share2, CheckCircle, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const Leaderboard = () => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [leaderboardRes, referralRes] = await Promise.all([
        creditsAPI.getLeaderboard(20),
        creditsAPI.getReferralStats()
      ]);
      setLeaderboard(leaderboardRes.data.leaderboard);
      setReferralStats(referralRes.data);
    } catch (error) {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/?ref=${referralStats?.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralStats?.referral_code || '');
    toast.success('Referral code copied!');
  };

  const shareReferral = () => {
    const link = `${window.location.origin}/?ref=${referralStats?.referral_code}`;
    const text = `Join TrafficFlow AI and get 150 bonus credits! Use my referral link: ${link}`;
    
    if (navigator.share) {
      navigator.share({ text, url: link });
    } else {
      navigator.clipboard.writeText(text);
      toast.success('Share text copied!');
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-300" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center font-mono font-bold text-gray-500">{rank}</span>;
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border-yellow-500/30';
    if (rank === 2) return 'bg-gradient-to-r from-gray-400/20 to-gray-500/20 border-gray-400/30';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600/20 to-orange-600/20 border-amber-600/30';
    return 'bg-void-paper border-white/10';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-neon-mint border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Find user's rank (with safety check)
  const userRank = leaderboard?.findIndex(l => l.user_id === user?.id) + 1 || 0;

  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-2xl text-white flex items-center gap-2">
          <Trophy className="w-6 h-6 text-neon-amber" />
          Leaderboard & Referrals
        </h1>
        <p className="text-gray-400">Top earners and your referral program</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Referral Card */}
        <Card className="lg:col-span-1 bg-gradient-to-br from-neon-mint/10 to-neon-violet/10 border-neon-mint/30">
          <CardHeader>
            <CardTitle className="text-white font-heading flex items-center gap-2">
              <Users className="w-5 h-5 text-neon-mint" />
              Your Referral Program
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-void/50 rounded-lg p-4 text-center">
                <p className="font-mono font-bold text-3xl text-neon-mint">
                  {referralStats?.total_referrals || 0}
                </p>
                <p className="text-gray-400 text-sm">Referrals</p>
              </div>
              <div className="bg-void/50 rounded-lg p-4 text-center">
                <p className="font-mono font-bold text-3xl text-neon-amber">
                  {referralStats?.referral_earnings || 0}
                </p>
                <p className="text-gray-400 text-sm">Credits Earned</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-gray-400 text-sm">Your Referral Code</label>
              <div className="flex gap-2">
                <Input
                  value={referralStats?.referral_code || ''}
                  readOnly
                  className="bg-void border-white/10 font-mono text-lg text-center text-neon-mint"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyReferralCode}
                  className="border-white/20 hover:bg-white/5"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={copyReferralLink}
                className="flex-1 bg-neon-mint text-black hover:bg-neon-mint/90 font-bold"
                data-testid="copy-referral-link"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
              <Button
                onClick={shareReferral}
                variant="outline"
                className="border-neon-violet text-neon-violet hover:bg-neon-violet/10"
              >
                <Share2 className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="bg-void/50 rounded-lg p-4 space-y-2">
              <p className="text-white font-medium text-sm">How it works:</p>
              <ul className="text-gray-400 text-sm space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-neon-mint" />
                  Share your referral link
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-neon-mint" />
                  Friend gets 150 credits (50 bonus!)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-neon-mint" />
                  You earn 50 credits per referral
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Leaderboard */}
        <Card className="lg:col-span-2 bg-void-paper border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white font-heading flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-neon-amber" />
                Top Earners
              </CardTitle>
              {userRank > 0 && (
                <span className="text-gray-400 text-sm">
                  Your rank: <span className="text-neon-mint font-bold">#{userRank}</span>
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(!leaderboard || leaderboard.length === 0) ? (
                <div className="text-center py-12">
                  <Trophy className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500">No data yet. Start earning to climb the ranks!</p>
                </div>
              ) : leaderboard.map((entry) => (
                <div
                  key={entry.user_id}
                  className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${getRankStyle(entry.rank)} ${
                    entry.user_id === user?.id ? 'ring-2 ring-neon-mint/50' : ''
                  }`}
                >
                  {/* Rank */}
                  <div className="w-10 flex justify-center">
                    {getRankIcon(entry.rank)}
                  </div>
                  
                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium truncate">
                        {entry.name}
                        {entry.user_id === user?.id && (
                          <span className="text-neon-mint ml-2">(You)</span>
                        )}
                      </p>
                      {entry.plan !== 'free' && (
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                          entry.plan === 'power' 
                            ? 'bg-neon-violet/20 text-neon-violet' 
                            : 'bg-neon-mint/20 text-neon-mint'
                        }`}>
                          {entry.plan.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-500 text-sm">
                      {entry.total_referrals} referrals
                    </p>
                  </div>
                  
                  {/* Credits */}
                  <div className="text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <Zap className="w-4 h-4 text-neon-amber" />
                      <span className="font-mono font-bold text-xl text-white">
                        {entry.total_earned.toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-500 text-xs">credits earned</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Leaderboard;
